// src/hooks/useStudentGoals.js
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config';

export function useStudentGoals() {
  const [showGoals, setShowGoals] = useState(false);
  const [goalsStudent, setGoalsStudent] = useState(null);
  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(false);

  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingGoalText, setEditingGoalText] = useState('');
  const [editingGoalTerm, setEditingGoalTerm] = useState('');
  const [editingGoalStatus, setEditingGoalStatus] = useState('');
  const [editingGoalDueDate, setEditingGoalDueDate] = useState('');

  const [goalToDelete, setGoalToDelete] = useState(null);

  const token = localStorage.getItem('token');

  const loadStudentGoals = async (studentId) => {
    try {
      setGoalsLoading(true);
      const res = await fetch(`${API_URL}/students/${studentId}/goals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load goals');
      const data = await res.json();
      setGoals(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load student goals');
      setGoals([]);
    } finally {
      setGoalsLoading(false);
    }
  };

  const openGoalsFor = async (student) => {
    setGoalsStudent(student);
    setShowGoals(true);
    await loadStudentGoals(student.id);
  };

  const addGoal = async ({ goal_text, term, status, due_date }) => {
    if (!goalsStudent) return;
    if (!goal_text?.trim()) return toast.warn('Please type a goal first');

    const payload = {
      goal_text: goal_text.trim(),
      term: term || 'Weekend',
      status: status || 'in-progress',
      due_date: due_date || null,
    };

    try {
      const { data } = await axios.post(
        `${API_URL}/students/${goalsStudent.id}/goals`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGoals((prev) => [data, ...prev]);
      toast.success('Goal added');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add goal');
    }
  };

  const startEditGoal = (goal) => {
    setEditingGoalId(goal.id);
    setEditingGoalText(goal.goal_text);
    setEditingGoalTerm(goal.term || '');
    setEditingGoalStatus(goal.status || 'in-progress');
    setEditingGoalDueDate(goal.due_date || '');
  };

  const cancelEditGoal = () => {
    setEditingGoalId(null);
    setEditingGoalText('');
    setEditingGoalTerm('');
    setEditingGoalStatus('');
    setEditingGoalDueDate('');
  };

  const saveGoal = async () => {
    if (!goalsStudent || !editingGoalId) return;
    if (!editingGoalText?.trim()) return toast.warn('Goal cannot be empty');

    const payload = {
      goal_text: editingGoalText.trim(),
      term: editingGoalTerm || 'Weekend',
      status: editingGoalStatus || 'in-progress',
      due_date: editingGoalDueDate || null,
    };

    try {
      const res = await fetch(
        `${API_URL}/students/${goalsStudent.id}/goals/${editingGoalId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error('Failed to update goal');
      const updated = await res.json();

      setGoals((prev) =>
        prev.map((g) =>
          g.id === editingGoalId
            ? updated?.id
              ? updated
              : { ...g, ...payload }
            : g
        )
      );

      cancelEditGoal();
      toast.success('Goal updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async () => {
    if (!goalsStudent || !goalToDelete) return;

    try {
      const res = await fetch(
        `${API_URL}/students/${goalsStudent.id}/goals/${goalToDelete.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error('Failed to delete goal');

      setGoals((prev) => prev.filter((g) => g.id !== goalToDelete.id));
      setGoalToDelete(null);
      toast.success('Goal deleted');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete goal');
    }
  };

  return {
    showGoals,
    setShowGoals,
    goalsStudent,
    goals,
    goalsLoading,

    editingGoalId,
    setEditingGoalId,
    editingGoalText,
    setEditingGoalText,
    editingGoalTerm,
    setEditingGoalTerm,
    editingGoalStatus,
    setEditingGoalStatus,
    editingGoalDueDate,
    setEditingGoalDueDate,

    goalToDelete,
    setGoalToDelete,

    openGoalsFor,
    addGoal,
    startEditGoal,
    cancelEditGoal,
    saveGoal,
    deleteGoal,
  };
}
